// SPDX-License-Identifier: GPL-3.0-only
// Copyright (C) 2026 Kyle Johnson
// This file is part of Hushold.
//
// Hushold is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as
// published by the Free Software Foundation, version 3.

/**
 * Hook to manage the crypto Web Worker lifecycle.
 * The worker is created once and reused for the session lifetime.
 */

import { useRef, useEffect } from 'react';

// Vite worker import — ?worker suffix tells Vite to bundle it as a worker module
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore — Vite processes this at build time
import CryptoWorker from '../workers/crypto.worker.ts?worker';

export function useCryptoWorker(): React.MutableRefObject<Worker | null> {
  const workerRef = useRef<Worker | null>(null);

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    workerRef.current = new CryptoWorker() as Worker;

    return () => {
      workerRef.current?.terminate();
      workerRef.current = null;
    };
  }, []);

  return workerRef;
}

import type { WrappedDek, Argon2idParams } from '../types.js';

// Argon2id parameters for wrapping the DEK under the password (SEC-01). Tuned for
// memory-hardness against offline cracking of a stolen server DB; ~64 MiB / a
// few hundred ms. Stored alongside the wrap so parameters can evolve.
export const ARGON2_DEFAULT_PARAMS: Argon2idParams = { t: 3, m: 64 * 1024, p: 1 };

function workerCall<T>(
  worker: Worker,
  msg: object,
  resultType: string,
  extract: (msg: Record<string, unknown>) => T,
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const id = `${resultType}-${Date.now()}-${Math.random()}`;
    const handler = (e: MessageEvent) => {
      const m = e.data as Record<string, unknown>;
      if (m['id'] !== id) return;
      worker.removeEventListener('message', handler);
      if (m['type'] === resultType) {
        resolve(extract(m));
      } else if (m['type'] === 'ERROR') {
        reject(new Error((m['message'] as string | undefined) ?? 'Crypto worker error'));
      }
    };
    worker.addEventListener('message', handler);
    worker.postMessage({ ...msg, id });
  });
}

/** Derives the 64-char hex serverAuthToken sent to the server instead of the raw password. */
export function deriveAuthTokenViaWorker(
  worker: Worker,
  password: string,
  username: string,
): Promise<string> {
  return workerCall(
    worker,
    { type: 'DERIVE_AUTH_TOKEN', password, username },
    'DERIVE_AUTH_TOKEN_RESULT',
    (m) => m['authToken'] as string,
  );
}

/** Generates a fresh random 32-byte Data Encryption Key. Returns raw key bytes. */
export function generateDekViaWorker(worker: Worker): Promise<ArrayBuffer> {
  return workerCall(
    worker,
    { type: 'GENERATE_DEK' },
    'GENERATE_DEK_RESULT',
    (m) => m['keyData'] as ArrayBuffer,
  );
}

/** Wraps the DEK under Argon2id(password, wrapSalt). Returns { iv, ciphertext } hex. */
export function wrapDekViaWorker(
  worker: Worker,
  dekData: ArrayBuffer,
  password: string,
  wrapSalt: Uint8Array,
  params: Argon2idParams = ARGON2_DEFAULT_PARAMS,
): Promise<{ iv: string; ciphertext: string }> {
  return workerCall(
    worker,
    { type: 'WRAP_DEK', dekData, password, wrapSalt: Array.from(wrapSalt), params },
    'WRAP_DEK_RESULT',
    (m) => ({ iv: m['iv'] as string, ciphertext: m['ciphertext'] as string }),
  );
}

/** Unwraps the DEK using Argon2id(password, blob.wrapSalt). Returns raw DEK bytes. */
export function unwrapDekViaWorker(
  worker: Worker,
  blob: WrappedDek,
  password: string,
  params: Argon2idParams = ARGON2_DEFAULT_PARAMS,
): Promise<ArrayBuffer> {
  return workerCall(
    worker,
    { type: 'UNWRAP_DEK', blob, password, params },
    'UNWRAP_DEK_RESULT',
    (m) => m['keyData'] as ArrayBuffer,
  );
}

/** Wraps the DEK under HKDF(mnemonic entropy) — the recovery path. Returns hex. */
export function wrapDekMnemonicViaWorker(
  worker: Worker,
  dekData: ArrayBuffer,
  mnemonicEntropy: Uint8Array,
): Promise<{ iv: string; ciphertext: string }> {
  return workerCall(
    worker,
    { type: 'WRAP_DEK_MNEMONIC', dekData, mnemonicEntropy: Array.from(mnemonicEntropy) },
    'WRAP_DEK_MNEMONIC_RESULT',
    (m) => ({ iv: m['iv'] as string, ciphertext: m['ciphertext'] as string }),
  );
}

/** Unwraps the DEK using HKDF(mnemonic entropy). Returns raw DEK bytes. */
export function unwrapDekMnemonicViaWorker(
  worker: Worker,
  blob: WrappedDek,
  mnemonicEntropy: Uint8Array,
): Promise<ArrayBuffer> {
  return workerCall(
    worker,
    { type: 'UNWRAP_DEK_MNEMONIC', blob, mnemonicEntropy: Array.from(mnemonicEntropy) },
    'UNWRAP_DEK_MNEMONIC_RESULT',
    (m) => m['keyData'] as ArrayBuffer,
  );
}

/** Derives the 64-char hex recovery proof from the mnemonic entropy bytes. */
export function deriveRecoveryProofViaWorker(
  worker: Worker,
  mnemonicEntropy: Uint8Array,
  username: string,
): Promise<string> {
  return workerCall(
    worker,
    { type: 'DERIVE_RECOVERY_PROOF', mnemonicEntropy: Array.from(mnemonicEntropy), username },
    'DERIVE_RECOVERY_PROOF_RESULT',
    (m) => m['proof'] as string,
  );
}

/** Encrypts a batch of events under the DEK into a wire-format blob. */
export function encryptEventsViaWorker(
  worker: Worker,
  events: import('../types.js').LedgerEvent[],
  keyData: ArrayBuffer,
): Promise<Uint8Array> {
  return workerCall(
    worker,
    { type: 'ENCRYPT', events, keyData },
    'ENCRYPT_RESULT',
    (m) => new Uint8Array(m['ciphertext'] as number[]),
  );
}

/** Decrypts a single wire-format blob (the cached snapshot) under the DEK. */
export function decryptSnapshotViaWorker(
  worker: Worker,
  blob: Uint8Array,
  keyData: ArrayBuffer,
): Promise<import('../types.js').LedgerEvent[]> {
  return workerCall(
    worker,
    { type: 'DECRYPT', blob: Array.from(blob), keyData },
    'DECRYPT_RESULT',
    (m) => m['events'] as import('../types.js').LedgerEvent[],
  );
}

/** AES-GCM encrypts a string under a raw key (the DEK). Returns hex { iv, ciphertext }. */
export function encryptStringViaWorker(
  worker: Worker,
  plaintext: string,
  keyData: ArrayBuffer,
): Promise<{ iv: string; ciphertext: string }> {
  return workerCall(
    worker,
    { type: 'ENCRYPT_STRING', plaintext, keyData },
    'ENCRYPT_STRING_RESULT',
    (m) => ({ iv: m['iv'] as string, ciphertext: m['ciphertext'] as string }),
  );
}

/** AES-GCM decrypts a string blob under a raw key (the DEK). Returns the plaintext. */
export function decryptStringViaWorker(
  worker: Worker,
  blob: { iv: string; ciphertext: string },
  keyData: ArrayBuffer,
): Promise<string> {
  return workerCall(
    worker,
    { type: 'DECRYPT_STRING', blob, keyData },
    'DECRYPT_STRING_RESULT',
    (m) => m['plaintext'] as string,
  );
}

/**
 * Build a fresh KeyMaterialBundle wrapping `dekData` under both the password and
 * the mnemonic entropy, plus a copy of the mnemonic words encrypted under the DEK
 * (so the phrase can be re-shown later without a server-side crackable copy).
 * Used at registration, recovery, and key rotation.
 */
export async function buildKeyMaterialBundle(
  worker: Worker,
  dekData: ArrayBuffer,
  password: string,
  mnemonicEntropy: Uint8Array,
  mnemonic: string,
): Promise<import('../types.js').KeyMaterialBundle> {
  const wrapSalt = crypto.getRandomValues(new Uint8Array(32));
  const wrapSaltHex = Array.from(wrapSalt).map((b) => b.toString(16).padStart(2, '0')).join('');
  const [byPw, byMn, selfBlob] = await Promise.all([
    wrapDekViaWorker(worker, dekData, password, wrapSalt),
    wrapDekMnemonicViaWorker(worker, dekData, mnemonicEntropy),
    encryptStringViaWorker(worker, mnemonic, dekData),
  ]);
  return {
    v: 1,
    kdf: 'argon2id',
    kdfParams: ARGON2_DEFAULT_PARAMS,
    wrappedByPassword: { wrapSalt: wrapSaltHex, iv: byPw.iv, ciphertext: byPw.ciphertext },
    wrappedByMnemonic: { iv: byMn.iv, ciphertext: byMn.ciphertext },
    mnemonicSelfBlob: { iv: selfBlob.iv, ciphertext: selfBlob.ciphertext },
  };
}

